import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { adminApi } from '../api'
import type { PolicyKey } from '../api/policy'
import { toastUtil } from '../../utils/toast'
import AdminSidebar from '../components/AdminSidebar'
import Markdown from '../../components/Markdown'

const ORDER: PolicyKey[] = ['terms', 'privacy', 'marketing']
/*
 * 편집 화면에 보여줄 이름.
 *
 * privacy 자리에 들어 있는 내용은 '개인정보처리방침' 전문이다. 예전 라벨인
 * '개인정보 수집 및 이용 동의'는 가입할 때 체크하는 동의 항목의 이름이라,
 * 어느 문서를 고치는 것인지 헷갈렸다. 실제 내용에 맞춰 부른다.
 */
const LABELS: Record<PolicyKey, string> = {
  terms: '이용약관',
  privacy: '개인정보처리방침',
  marketing: '마케팅 정보 수신 동의',
}

const AdminPolicies: React.FC = () => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [previewKey, setPreviewKey] = useState<string | null>(null)

  const fetchPolicies = useCallback(async () => {
    setIsLoading(true)
    try {
      const res = await adminApi.getPolicies()
      const map: Record<string, string> = {}
      res.data.forEach((p) => {
        map[p.key] = p.content
      })
      setDrafts(map)
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login')
      } else {
        toastUtil.error('약관을 불러오는데 실패했습니다')
      }
    } finally {
      setIsLoading(false)
    }
  }, [navigate])

  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token')
    if (!adminToken) {
      navigate('/admin/login')
      return
    }
    fetchPolicies()
  }, [navigate, fetchPolicies])

  const handleLogout = () => {
    localStorage.removeItem('admin_token')
    navigate('/admin/login')
  }

  const handleSave = async (key: PolicyKey) => {
    const content = drafts[key] ?? ''
    if (!content.trim()) {
      toastUtil.error('내용을 입력해주세요')
      return
    }
    setSavingKey(key)
    try {
      await adminApi.updatePolicy(key, { content })
      toastUtil.success(`${LABELS[key]} 저장되었습니다`)
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login')
      } else {
        toastUtil.error(
          error.response?.data?.message || '저장 중 오류가 발생했습니다'
        )
      }
    } finally {
      setSavingKey(null)
    }
  }

  return (
    <div className="min-h-screen bg-backgroundColorBg2 flex">
      <AdminSidebar />

      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-brandPrimary-10% sticky top-0 z-10">
          <div className="px-24px py-16px flex items-center justify-end">
            <button
              onClick={handleLogout}
              className="text-style-button2 text-textColorText2 hover:text-brown-80%-main transition-colors"
            >
              로그아웃
            </button>
          </div>
        </header>

        <main className="flex-1 px-24px py-32px">
          <h2 className="text-style-title2 text-textColorText2 mb-8px">
            약관 관리
          </h2>
          <p className="text-style-captionL text-textColorText2 mb-24px">
            마크다운으로 작성합니다 — <code>## 제목</code>, <code>### 소제목</code>,{' '}
            <code>- 목록</code>, <code>**굵게**</code>
          </p>

          {isLoading ? (
            <div className="py-80px text-center text-style-body2 text-textColorText2">
              로딩 중...
            </div>
          ) : (
            <div className="flex flex-col gap-24px max-w-[900px]">
              {ORDER.map((key) => (
                <div key={key} className="bg-white rounded-16px shadow-sm p-24px">
                  <div className="flex items-center justify-between mb-16px">
                    <h3 className="font-pretendard font-bold text-18px text-textColorText2">
                      {LABELS[key]}
                    </h3>
                    <button
                      type="button"
                      onClick={() =>
                        setPreviewKey(previewKey === key ? null : key)
                      }
                      className="text-style-button2 text-brown-80%-main hover:opacity-70"
                    >
                      {previewKey === key ? '편집' : '미리보기'}
                    </button>
                  </div>

                  {previewKey === key ? (
                    <div className="border border-brandPrimary-10% rounded-8px p-16px min-h-[300px] max-h-[500px] overflow-y-auto bg-backgroundColorBg2">
                      <Markdown content={drafts[key] ?? ''} />
                    </div>
                  ) : (
                    <textarea
                      value={drafts[key] ?? ''}
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [key]: e.target.value,
                        }))
                      }
                      spellCheck={false}
                      className="w-full h-[400px] border border-brandPrimary-10% rounded-8px p-16px text-14px leading-relaxed text-textColorText2 resize-y font-mono"
                    />
                  )}

                  <div className="mt-16px flex justify-end">
                    <button
                      type="button"
                      onClick={() => handleSave(key)}
                      disabled={savingKey === key}
                      className={`
                        py-12px
                        px-24px
                        bg-4B3621-80%
                        text-white
                        rounded-8px
                        text-style-button2
                        hover:bg-4B3621-80%-70%
                        transition-colors
                        disabled:bg-4B3621-80%-20%
                        disabled:cursor-not-allowed
                      `}
                    >
                      {savingKey === key ? '저장 중...' : '저장'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  )
}

export default AdminPolicies
